import { useEffect, useState } from "react";

// Puerta premium del generador de CV (0066).
//
// Esta app es 100% cliente y NO tiene cuenta propia: nunca ha hablado con la
// api. Al pasar a ser una función de pago, lo mínimo que necesita es saber si
// quien la abre tiene derecho, y eso lo dice `GET /api/premium/access`, que
// acepta peticiones anónimas y devuelve el veredicto YA CALCULADO (interruptor
// apagado ⇒ pasa todo el mundo; admin ⇒ pasa siempre; pase vigente ⇒ pasa).
//
// ⚠ **Alcance honesto de esta puerta.** El bundle y el wasm del compilador se
// sirven en abierto desde Pages, así que esto no impide que alguien decidido
// se descargue los ficheros y ejecute el compilador por su cuenta. Lo que hace
// es cerrar la HERRAMIENTA: sin acceso, la aplicación no arranca. Es la misma
// clase de protección que tiene cualquier aplicación de cliente, y meter el
// compilador detrás de la api significaría rehacer el producto entero
// (27 MiB de wasm que hoy corren en el navegador). Se asume a conciencia.
//
// La cookie es `__Host-session`, host-only de api.elcybercurioso.com, así que
// va con `credentials: "include"` y NO se lee de `document.cookie` (§8).

const API_URL = import.meta.env.VITE_API_URL ?? "https://api.elcybercurioso.com";
const WEB_URL = import.meta.env.VITE_BLOG_URL ?? "https://elcybercurioso.com";
const FEATURE = "cv";

interface AccessResponse {
  enabled: boolean;
  allowed: boolean;
  isAdmin: boolean;
  features: Record<string, boolean>;
}

type State = { status: "checking" } | { status: "allowed" } | { status: "locked" };

export function usePremiumAccess(): State {
  const [state, setState] = useState<State>({ status: "checking" });

  useEffect(() => {
    let alive = true;
    fetch(`${API_URL}/api/premium/access`, {
      credentials: "include",
      headers: { accept: "application/json" },
    })
      .then((r) => (r.ok ? (r.json() as Promise<AccessResponse>) : Promise.reject(r.status)))
      .then((j) => {
        if (!alive) return;
        // El interruptor apagado abre la puerta: es la misma semántica que
        // `requirePremium()` en el servidor, y el endpoint ya la aplica en
        // `allowed`. Se comprueba también la clave concreta por si algún día
        // el catálogo deja de incluir "cv".
        const ok = !j.enabled || j.allowed || j.features?.[FEATURE] === true;
        setState({ status: ok ? "allowed" : "locked" });
      })
      // Un fallo de red NO cierra la herramienta. Aquí no se protege un
      // secreto —los ficheros ya están servidos—, así que castigar un hipo de
      // la api dejando fuera a quien sí ha pagado sería el peor de los dos
      // errores posibles.
      .catch(() => { if (alive) setState({ status: "allowed" }); });
    return () => { alive = false; };
  }, []);

  return state;
}

export function PremiumGate({ children }: { children: React.ReactNode }) {
  const state = usePremiumAccess();

  if (state.status === "checking") {
    return (
      <div className="flex min-h-dvh items-center justify-center p-6">
        <p className="font-mono text-sm text-mute">comprobando acceso…</p>
      </div>
    );
  }

  if (state.status === "locked") {
    return (
      <div className="flex min-h-dvh items-center justify-center p-6">
        <div className="w-full max-w-lg space-y-4 rounded border border-accent bg-panel p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-accent">
            ▌ contenido premium
          </div>
          <h1 className="font-serif text-2xl italic text-ink">CV Generator</h1>
          <p className="text-sm text-mute">
            El editor de currículum forma parte del acceso premium: plantillas
            listas para filtros ATS, vista previa en vivo y descarga en PDF.
          </p>
          <p className="text-xs text-mute">
            Se compra como un pase con caducidad y los días se acumulan si ya
            tienes uno. No hay cobros recurrentes.
          </p>
          <div className="flex flex-wrap gap-2">
            <a
              href={`${WEB_URL}/premium`}
              className="min-h-[36px] rounded border border-accent bg-accent/10 px-4 py-2 font-mono text-sm text-accent transition-colors hover:bg-accent hover:text-bg"
            >
              ver precios →
            </a>
            <a
              href={WEB_URL}
              className="min-h-[36px] rounded border border-line px-4 py-2 font-mono text-sm text-mute transition-colors hover:border-accent hover:text-accent"
            >
              volver al sitio
            </a>
          </div>
          <p className="font-mono text-xs text-mute">
            Si ya tienes pase, inicia sesión en el sitio principal y vuelve a
            abrir esta página.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
